import { useEffect, useMemo, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, RotateCcw, Minus, Plus, Info, MapPinned, ChevronDown, ChevronUp } from 'lucide-react';
import type { CoverageBounds, LngLat, StationAvailability, TurnoutProvenance } from '@duty/shared';
import { MAPBOX_TOKEN } from '@/config/mapbox';
import { useTheme } from '@/theme/theme';
import { useCoverage, emptyCoverageOverrides, type CoverageOverrides, type CoverageQuery, type CoverageShareEntry } from '@/hooks/useCoverage';
import { useStoredTurnoutOverrides, pickStationOverrides } from '@/hooks/useTurnoutOverrides';
import { candidateStationsInBounds, graphBounds, graphCenter, turnoutProfilesFor, cellSizeForBounds } from '@/incident/coverage';
import { useCoverageMap, assignStationColors, stationColor } from './useCoverageMap';

/** Stations within the viewport PLUS this fraction of its width/height on each side are analysed —
 * so a cell near the edge of the visible map can still be correctly attributed to a station just
 * off-screen, instead of only ever seeing whoever happens to be exactly in view. */
const STATION_BUFFER_RATIO = 0.5;

/**
 * Coverage-planning mode: which brigade effectively covers this area under
 * current turn-out assumptions, with no specific incident point. Answers
 * "what if Bungendore takes 7 min to leave and Wamboin takes 20?" as a
 * planning question, not an incident question. See
 * docs/response-coverage-2.0.md — this is the "effective-coverage preview"
 * half of the feature; CoverageAnalysisView is the "response-order" half.
 *
 * Stations and the coverage grid are both re-derived from the map's actual
 * visible viewport (on `moveend`) rather than a fixed corridor centre — so
 * panning/zooming shows the brigades and coverage for wherever the user is
 * actually looking, not a static starting bbox.
 *
 * When `onCreateIncidentAt` is provided, clicking anywhere on the map models
 * an incident scenario there — answers "what would the actual response order
 * and navigation assumptions look like if it happened right here?" without
 * leaving the planning tool.
 */
export function CoveragePlanningView({
  onClose,
  onCreateIncidentAt,
}: {
  onClose: () => void;
  onCreateIncidentAt?: (point: LngLat) => void;
}) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [overrides, setOverrides] = useState<CoverageOverrides>(() => emptyCoverageOverrides());
  const [sheetOpen, setSheetOpen] = useState(false);

  const initialCenter = useMemo(() => graphCenter(), []);
  const [viewportBounds, setViewportBounds] = useState<CoverageBounds>(() => graphBounds());

  const stations = useMemo(
    () => candidateStationsInBounds(viewportBounds, { bufferRatio: STATION_BUFFER_RATIO }),
    [viewportBounds],
  );
  const gridBounds = useMemo<CoverageBounds>(() => {
    const b = viewportBounds;
    const padLng = (b.maxLng - b.minLng) * 0.1;
    const padLat = (b.maxLat - b.minLat) * 0.1;
    return { minLng: b.minLng - padLng, minLat: b.minLat - padLat, maxLng: b.maxLng + padLng, maxLat: b.maxLat + padLat };
  }, [viewportBounds]);
  const cellSizeDeg = useMemo(() => cellSizeForBounds(gridBounds), [gridBounds]);
  const { storedOverrides, saveOverride } = useStoredTurnoutOverrides();
  const baseTurnoutProfiles = useMemo(
    () => ({ ...turnoutProfilesFor(stations), ...pickStationOverrides(storedOverrides, stations) }), // shared SME defaults win over the static seed profile
    [stations, storedOverrides],
  );
  const query = useMemo<CoverageQuery>(
    () => ({ stations, turnoutProfiles: baseTurnoutProfiles }), // no incidentPoint — area only
    [stations, baseTurnoutProfiles],
  );
  const { grid, gridBorders, coverageShare, loading, graphSource, error } = useCoverage(query, overrides, gridBounds, cellSizeDeg);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mapRef = useCoverageMap({
    containerRef,
    theme,
    center: initialCenter,
    zoom: 10,
    stations,
    grid,
    gridBorders,
    onMapClick: onCreateIncidentAt,
  });

  // Recompute stations + grid from wherever the user actually pans/zooms to.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMoveEnd = () => {
      const b = map.getBounds();
      if (!b) return;
      setViewportBounds({ minLng: b.getWest(), minLat: b.getSouth(), maxLng: b.getEast(), maxLat: b.getNorth() });
    };
    map.on('moveend', onMoveEnd);
    if (map.isStyleLoaded()) onMoveEnd();
    else map.once('load', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  const colorMap = useMemo(() => assignStationColors(stations), [stations]);
  const shareByStation = useMemo(() => new Map((coverageShare ?? []).map((s) => [s.stationId, s])), [coverageShare]);
  const sortedStations = useMemo(
    () =>
      [...stations].sort(
        (a, b) => (shareByStation.get(b.id)?.pct ?? 0) - (shareByStation.get(a.id)?.pct ?? 0),
      ),
    [stations, shareByStation],
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg" role="dialog" aria-modal="true" aria-label="Coverage planning">
      <div className="flex items-center justify-between border-b border-border bg-surface/70 px-4 py-2.5">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold">
            <MapPinned size={14} /> Coverage planning
          </h2>
          <p className="text-xs text-muted">
            Effective coverage by turn-out assumption — planning tool, no specific incident. Modelled, not live traffic.
            {onCreateIncidentAt ? ' Click the map to model an incident there.' : ''}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md border border-border bg-surface-2 p-1.5 text-muted hover:text-accent"
          aria-label="Close coverage planning"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_22rem]">
        <div className="relative min-h-[45vh] lg:min-h-0">
          {MAPBOX_TOKEN ? (
            <div ref={containerRef} className="h-full w-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted">Map unavailable</div>
          )}
          {loading && (
            <div className="absolute left-3 top-3 rounded-md bg-bg/80 px-2 py-1 text-[11px] text-muted">Recomputing…</div>
          )}
          <div className="absolute right-3 top-3 rounded-md bg-bg/80 px-2 py-1 text-[11px] text-muted">
            {stations.length} brigades in view + buffer
          </div>
        </div>

        <aside className="flex min-h-0 flex-col border-t border-border lg:border-t-0 lg:border-l">
          <button
            onClick={() => setSheetOpen((o) => !o)}
            className="flex items-center justify-between px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted lg:hidden"
            aria-expanded={sheetOpen}
          >
            <span>Effective coverage share ({stations.length})</span>
            {sheetOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>

          <div
            className={`min-h-0 flex-col overflow-y-auto p-3.5 pt-0 lg:flex lg:pt-3.5 ${sheetOpen ? 'flex max-h-[42vh] lg:max-h-none' : 'hidden'}`}
            style={{ paddingBottom: `max(0.875rem, env(safe-area-inset-bottom))` }}
          >
            {error && <p className="mb-2 text-xs text-crit">{error}</p>}

            <h3 className="mb-2 hidden text-xs font-semibold uppercase tracking-wide text-muted lg:block">
              Effective coverage share
            </h3>
            <p className="mb-2 text-[10px] text-muted">Pan or zoom the map to see coverage for a different area.</p>
            <ul className="space-y-2">
              {sortedStations.map((s) => (
                <PlanningStationRow
                  key={s.id}
                  stationId={s.id}
                  stationName={s.name}
                  region={s.region}
                  color={stationColor(s.id, colorMap)}
                  share={shareByStation.get(s.id)}
                  baseTurnoutMinutes={baseTurnoutProfiles[s.id]?.baseMinutes}
                  overrides={overrides}
                  onOverridesChange={setOverrides}
                  onSaveOverride={saveOverride}
                />
              ))}
            </ul>

            <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
              <button
                onClick={() => setOverrides(emptyCoverageOverrides())}
                className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-accent"
              >
                <RotateCcw size={11} /> Reset scenario overrides (shared defaults stay)
              </button>
            </div>

            <div className="mt-3 flex items-start gap-1.5 border-t border-border pt-2.5 text-[10px] leading-relaxed text-muted">
              <Info size={11} className="mt-0.5 shrink-0" />
              <span>
                Modelled travel time from a synthetic seed road graph — not live traffic, not brigade-confirmed turn-out.
                {graphSource ? ` Source: ${graphSource}.` : ''} Coverage share is the % of this area's grid cells each
                brigade wins on current assumptions. Turn-out changes save as the shared default for every user.
                See docs/response-coverage-2.0.md.
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PlanningStationRow({
  stationId,
  stationName,
  region,
  color,
  share,
  baseTurnoutMinutes,
  overrides,
  onOverridesChange,
  onSaveOverride,
}: {
  stationId: string;
  stationName: string;
  region: string | undefined;
  color: string;
  share: CoverageShareEntry | undefined;
  /** Effective default (shared SME override, or seed) before any local, unsaved slider tweak. */
  baseTurnoutMinutes: number | undefined;
  overrides: CoverageOverrides;
  onOverridesChange: (o: CoverageOverrides) => void;
  onSaveOverride: (stationId: string, baseMinutes: number) => void;
}) {
  const override = overrides.turnout[stationId];
  const currentTurnout = override?.baseMinutes ?? baseTurnoutMinutes;
  const availability = overrides.availability[stationId]?.state ?? 'unknown';
  const pct = share?.pct ?? 0;

  const setTurnout = (minutes: number) => {
    const baseMinutes = Math.max(0, minutes);
    onOverridesChange({
      ...overrides,
      turnout: {
        ...overrides.turnout,
        [stationId]: {
          baseMinutes,
          provenance: 'sme-entered' as TurnoutProvenance,
          capturedAt: new Date().toISOString(),
        },
      },
    });
    onSaveOverride(stationId, baseMinutes);
  };

  const cycleAvailability = () => {
    const next: StationAvailability['state'] =
      availability === 'unknown' ? 'available' : availability === 'available' ? 'committed' : 'unknown';
    onOverridesChange({
      ...overrides,
      availability: { ...overrides.availability, [stationId]: { state: next, capturedAt: new Date().toISOString() } },
    });
  };

  return (
    <li className={`rounded-md border p-2 text-xs ${availability === 'committed' ? 'border-border/60 bg-surface-2/40 opacity-60' : 'border-border bg-surface-2/60'}`}>
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
        <span className="min-w-0 flex-1 truncate font-medium">{stationName}</span>
        <span className="shrink-0 font-mono text-[11px] text-muted">{pct.toFixed(0)}%</span>
      </div>
      {region && <div className="mt-0.5 pl-4 text-[10px] text-muted">{region}</div>}
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[10px] text-muted">Turn-out</span>
          <button
            onClick={() => setTurnout((currentTurnout ?? 5) - 1)}
            className="rounded border border-border p-0.5 text-muted hover:text-accent"
            aria-label={`Decrease ${stationName} turn-out`}
          >
            <Minus size={10} />
          </button>
          <span className="w-6 text-center font-mono">{currentTurnout !== undefined ? Math.round(currentTurnout) : '5'}</span>
          <button
            onClick={() => setTurnout((currentTurnout ?? 5) + 1)}
            className="rounded border border-border p-0.5 text-muted hover:text-accent"
            aria-label={`Increase ${stationName} turn-out`}
          >
            <Plus size={10} />
          </button>
        </div>
        <button
          onClick={cycleAvailability}
          className={`rounded-full border px-2 py-0.5 text-[10px] capitalize ${
            availability === 'available'
              ? 'border-ok/50 bg-ok/10 text-ok'
              : availability === 'committed'
                ? 'border-med/50 bg-med/10 text-med'
                : 'border-border text-muted'
          }`}
        >
          {availability}
        </button>
      </div>
    </li>
  );
}
